<script>
// FilterSelector.vue
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import ReportsFiltersDateRange from './Filters/DateRange.vue';
import ReportsFiltersDateGroupBy from './Filters/DateGroupBy.vue';
import ReportsFiltersAgents from './Filters/Agents.vue';
import ReportsFiltersLabels from './Filters/Labels.vue';
import ReportsFiltersInboxes from './Filters/Inboxes.vue';
import ReportsFiltersTeams from './Filters/Teams.vue';
import ReportsFiltersRatings from './Filters/Ratings.vue';
import subDays from 'date-fns/subDays';
import subHours from 'date-fns/subHours';
import { DATE_RANGE_OPTIONS } from '../constants';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import getUnixTime from 'date-fns/getUnixTime';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

export default {
  components: {
    WootDateRangePicker,
    ReportsFiltersDateRange,
    ReportsFiltersDateGroupBy,
    ReportsFiltersAgents,
    ReportsFiltersLabels,
    ReportsFiltersInboxes,
    ReportsFiltersTeams,
    ReportsFiltersRatings,
    ToggleSwitch,
  },
  props: {
    showGroupByFilter: {
      type: Boolean,
      default: false,
    },
    showAgentsFilter: {
      type: Boolean,
      default: false,
    },
    showLabelsFilter: {
      type: Boolean,
      default: false,
    },
    showInboxFilter: {
      type: Boolean,
      default: false,
    },
    showRatingFilter: {
      type: Boolean,
      default: false,
    },
    showTeamFilter: {
      type: Boolean,
      default: false,
    },
    showBusinessHoursSwitch: {
      type: Boolean,
      default: true,
    },
    defaultTeamSelection: {
      type: Boolean,
      default: false,
    },
    maxDateRangeOption: {
      type: String,
      default: null,
    },
  },
  emits: ['filterChange'],
  data() {
    return {
      selectedDateRange: DATE_RANGE_OPTIONS[this.getDefaultDateRangeOption()],
      selectedGroupByFilter: null,
      selectedLabel: null,
      selectedInbox: null,
      selectedTeam: null,
      selectedRating: null,
      selectedAgents: [],
      customDateRange: [new Date(), new Date()],
      businessHoursSelected: false,
      teamFilterReady: false,
      teamMembers: [], // Armazena os membros do time selecionado
    };
  },
  computed: {
    isDateRangeSelected() {
      return (
        this.selectedDateRange.id === DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE.id
      );
    },
    isHourBasedRange() {
      return !!this.selectedDateRange.hours;
    },
    isGroupByPossible() {
      return this.selectedDateRange.id !== DATE_RANGE_OPTIONS.LAST_7_DAYS.id;
    },
    to() {
      if (this.isDateRangeSelected) {
        return getUnixEndOfDay(this.customDateRange[1]);
      }
      
      if (this.isHourBasedRange) {
        return getUnixTime(new Date());
      }
      
      return getUnixEndOfDay(new Date());
    },
    from() {
      if (this.isDateRangeSelected) {
        return getUnixStartOfDay(this.customDateRange[0]);
      }

      if (this.isHourBasedRange) {
        const fromDate = subHours(new Date(), this.selectedDateRange.hours);
        return getUnixTime(fromDate);
      }

      const { offset } = this.selectedDateRange;
      const fromDate = subDays(new Date(), offset);
      return getUnixStartOfDay(fromDate);
    },
    validGroupOptions() {
      return this.selectedDateRange.groupByOptions;
    },
    validGroupBy() {
      if (!this.selectedGroupByFilter) {
        return this.validGroupOptions[0];
      }

      const validIds = this.validGroupOptions.map(opt => opt.id);
      if (validIds.includes(this.selectedGroupByFilter.id)) {
        return this.selectedGroupByFilter;
      }
      return this.validGroupOptions[0];
    },
    
    // 🔥 COMPUTED: Filtra agentes do time selecionado
    filteredAgentIds() {
      if (!this.selectedTeam || this.teamMembers.length === 0) {
        return null; // Retorna null para mostrar todos os agentes
      }
      
      // Retorna array de IDs dos agentes que pertencem ao time
      // member.id representa o user_id do agente
      return [...new Set(this.teamMembers.map(member => member.id))];
    },
    
    // 🔥 COMPUTED: Filtra inboxes do time selecionado
    filteredInboxIds() {
      if (!this.selectedTeam || this.teamMembers.length === 0) {
        return null; // Retorna null para mostrar todas as inboxes
      }
      
      // Retorna array de IDs das inboxes únicas do time
      const inboxIds = this.teamMembers
        .filter(member => member.inbox_id)
        .map(member => member.inbox_id);
      
      return [...new Set(inboxIds)];
    },
  },
  
  watch: {
    // 🔥 WATCH: Monitora mudanças no time selecionado
    async selectedTeam(newTeam, oldTeam) {
      if (newTeam?.id !== oldTeam?.id) {
        // Limpa os filtros dependentes quando trocar de time
        this.selectedAgents = [];
        this.selectedInbox = null;
        
        // Carrega membros do novo time
        if (newTeam?.id) {
          await this.loadTeamMembers(newTeam.id);
        } else {
          this.teamMembers = [];
        }
      }
    },
  },

  mounted() {
    if (!this.defaultTeamSelection || !this.showTeamFilter) {
      this.emitChange();
    }
  },
  
  methods: {

    getDefaultDateRangeOption() {
      // Se maxDateRangeOption for LAST_7_DAYS ou menor, inicia com LAST_1_HOUR
      if (this.maxDateRangeOption === 'LAST_7_DAYS' || 
          this.maxDateRangeOption === 'LAST_24_HOURS' ||
          this.maxDateRangeOption === 'LAST_12_HOURS' ||
          this.maxDateRangeOption === 'LAST_6_HOURS' ||
          this.maxDateRangeOption === 'LAST_2_HOURS' ||
          this.maxDateRangeOption === 'LAST_1_HOUR') {
        return 'LAST_1_HOUR';
      }
      
      // Caso contrário, usa LAST_7_DAYS como padrão
      return 'LAST_7_DAYS';
    },
    
    // 🔥 MÉTODO: Carrega membros do time selecionado
    async loadTeamMembers(teamId) {
      try {
        
        // Carrega membros do time
        await this.$store.dispatch('teamMembers/get', { teamId });
        
        // Busca membros do store
        const members = this.$store.getters['teamMembers/getTeamMembers'](teamId);
        
        
        this.teamMembers = members || [];
        
        // Extrai IDs únicos de agentes e inboxes
        const agentIds = [...new Set(members.map(m => m.id))];
        const inboxIds = [...new Set(members.filter(m => m.inbox_id).map(m => m.inbox_id))];
        
        
      } catch (error) {
        console.error('❌ Erro ao carregar team members:', error);
        this.teamMembers = [];
      }
    },
    
    emitChange() {
      const {
        from,
        to,
        selectedGroupByFilter: groupBy,
        businessHoursSelected: businessHours,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      } = this;
      
      this.$emit('filterChange', {
        from,
        to,
        groupBy,
        businessHours,
        selectedAgents,
        selectedLabel,
        selectedInbox,
        selectedTeam,
        selectedRating,
      });
    },
    
    onDateRangeChange(selectedRange) {
      this.selectedDateRange = selectedRange;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    
    onCustomDateRangeChange(value) {
      this.customDateRange = value;
      this.selectedGroupByFilter = this.validGroupBy;
      this.emitChange();
    },
    
    onGroupingChange(payload) {
      this.selectedGroupByFilter = payload;
      this.emitChange();
    },
    
    handleAgentsFilterSelection(selectedAgents) {
      this.selectedAgents = selectedAgents;
      this.emitChange();
    },
    
    handleLabelsFilterSelection(selectedLabel) {
      this.selectedLabel = selectedLabel;
      this.emitChange();
    },
    
    handleInboxFilterSelection(selectedInbox) {
      this.selectedInbox = selectedInbox;
      this.emitChange();
    },
    
    async handleTeamFilterSelection(selectedTeam) {
      this.selectedTeam = selectedTeam;
      
      if (this.defaultTeamSelection && !this.teamFilterReady) {
        this.teamFilterReady = true;
      }
      
      this.emitChange();
    },
    
    handleRatingFilterSelection(selectedRating) {
      this.selectedRating = selectedRating;
      this.emitChange();
    },
  },
};
</script>

<template>
  <div class="flex flex-col justify-between gap-3 md:flex-row">
    <div
      class="w-full grid gap-y-2 gap-x-1.5 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]"
    >
      <ReportsFiltersDateRange
        :default-option="getDefaultDateRangeOption()"
        :max-date-range-option="maxDateRangeOption"
        @on-range-change="onDateRangeChange"
      />
      <WootDateRangePicker
        v-if="isDateRangeSelected"
        show-range
        class="no-margin auto-width"
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateRangeChange"
      />
      <ReportsFiltersDateGroupBy
        v-if="showGroupByFilter && isGroupByPossible"
        :valid-group-options="validGroupOptions"
        :selected-option="selectedGroupByFilter"
        @on-grouping-change="onGroupingChange"
      />
      
      <!-- 🔥 TEAM FILTER - Deve vir ANTES dos outros filtros -->
      <ReportsFiltersTeams
        v-if="showTeamFilter"
        :select-first-option="defaultTeamSelection"
        @team-filter-selection="handleTeamFilterSelection"
      />
      
      <!-- 🔥 AGENTS FILTER - Recebe filteredAgentIds -->
      <ReportsFiltersAgents
        v-if="showAgentsFilter"
        :filtered-agent-ids="filteredAgentIds"
        @agents-filter-selection="handleAgentsFilterSelection"
      />
      
      <!-- 🔥 INBOXES FILTER - Recebe filteredInboxIds -->
      <ReportsFiltersInboxes
        v-if="showInboxFilter"
        :filtered-inbox-ids="filteredInboxIds"
        @inbox-filter-selection="handleInboxFilterSelection"
      />
      
      <ReportsFiltersLabels
        v-if="showLabelsFilter"
        @labels-filter-selection="handleLabelsFilterSelection"
      />
      
      <ReportsFiltersRatings
        v-if="showRatingFilter"
        @rating-filter-selection="handleRatingFilterSelection"
      />
    </div>
    <div v-if="showBusinessHoursSwitch" class="flex items-center">
      <span class="mx-2 text-sm whitespace-nowrap">
        {{ $t('REPORT.BUSINESS_HOURS') }}
      </span>
      <span>
        <ToggleSwitch v-model="businessHoursSelected" @change="emitChange" />
      </span>
    </div>
  </div>
</template>